import { AvField, AvForm } from "availity-reactstrap-validation";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bounce, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Button, Card, CardBody, Col, Label, Row } from "reactstrap";
import api from "../../services/api";

const AddRefunds = () => {
  const navigate = useNavigate();
  document.title = "Technical Hub | Add Refunds";
const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [formData, setFormData] = useState({
    payment_id: "",
    roll_no: "",
    reg_id: "",
    name: "",
    course: "", // This will store course ID for submission
    technology: "", // This will store technology ID for submission
    refund_amount: "",
    term: "",
    payment_date: "",
    refund_reason: ""
  });

  // State for display purposes only
  const [displayData, setDisplayData] = useState({
    course_name: "", // Display only
    technology_name: "" // Display only
  });

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [paymentFetched, setPaymentFetched] = useState(false);

  // Handle input changes for editable fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Handle file input change
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // ✅ Fetch payment details using payment_id
 const fetchPaymentDetails = async () => {
    if (!formData.payment_id.trim()) {
      toast.error("Please enter a Payment ID.");
      return;
    }

    try {
      setFetching(true);
      const response = await api.post(`${backendUrl}get-refund-by-payment-id`,
        { payment_id: formData.payment_id.trim() }
      );

      console.log("API Response:", response.data); // For debugging

      // Check if the request was successful
      if (response.data.success) {
        // Check if there's refund data in the response
        if (response.data.refundData) {
          // If refund exists, show refund information
          toast.error(`Refund Alert: ${response.data.message}`, {
            duration: 5000, // Longer duration for important messages
          });

          // Optional: You might want to show refund details in a modal or form
          console.log("Refund details:", response.data.refundData);
          setFetching(false);
          return; // Don't proceed to fill form since refund exists
        }

        // If successful and no refund exists, proceed with payment data
        const data = response.data.data;

        // Set form data for backend submission (IDs)
        setFormData(prevState => ({
          ...prevState,
          roll_no: data.roll_no || "",
          reg_id: data.reg_id || "",
          name: data.first_name || data.name || "",
          course: data.course_id || data.course || "", // Store course ID
          term: data.term_name || data.term || "",
          refund_amount: data.amount || "",
          technology: data.technology_id || data.technology || "", // Store technology ID
          payment_date: data.created_at
            ? new Date(data.created_at).toISOString().split("T")[0]
            : data.payment_date || "",
          refund_reason: prevState.refund_reason
        }));

        // Set display data for UI (Names)
        setDisplayData({
          course_name: data.course_name || "",
          technology_name: data.technology_name || ""
        });

        setPaymentFetched(true);
        toast.success(response.data.message || "Payment details fetched successfully!");
      } else {
        // If success is false but we have a message
        if (response.data.message) {
          toast.error(response.data.message);
        } else {
          toast.error("Failed to fetch payment details. Please check the ID.");
        }
      }
    } catch (error) {
      console.error("Error fetching payment details:", error);

      // Extract error message from backend response
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.error ||
                          "Failed to fetch payment details. Please check the ID.";

      // Check if it's a refund-related error (400 status)
      if (error.response?.status === 400 && error.response?.data?.refundData) {
        // Handle refund case specifically
        const refundMsg = error.response.data.message || "Refund has already been created for this payment ID";
        toast.error(refundMsg, {
          duration: 5000,
        });

        // Optional: You can display refund details in your UI
        console.log("Refund details from error:", error.response.data.refundData);
      } else {
        // Handle other errors
        toast.error(errorMessage);
      }

      // Log full error for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log("Full error response:", error.response?.data);
      }
    } finally {
      setFetching(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (event, errors) => {
    event.preventDefault();

    if (errors && errors.length > 0) {
      toast.error("Please fix the form errors before submitting.");
      return;
    }

    // Validate that payment details were fetched
    if (!paymentFetched) {
      toast.error("Please fetch payment details first using the Payment ID.");
      return;
    }

    // Validate refund reason
    if (!formData.refund_reason.trim()) {
      toast.error("Please enter a refund reason.");
      return;
    }

    // Validate refund amount
    if (!formData.refund_amount || formData.refund_amount <= 0) {
      toast.error("Please enter a valid refund amount.");
      return;
    }

    // Validate that we have course and technology IDs
    if (!formData.course || !formData.technology) {
      toast.error("Course or Technology information is missing. Please fetch payment details again.");
      return;
    }

    setLoading(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();

      // Append all form fields that match backend expectations
      submitData.append("roll_no", formData.roll_no || "");
      submitData.append("reg_id", formData.reg_id || "");
      submitData.append("name", formData.name || "");
      submitData.append("course", formData.course || ""); // course ID will be sent
      submitData.append("technology", formData.technology || ""); // technology ID will be sent
      submitData.append("payment_id", formData.payment_id || "");
      submitData.append("refund_amount", formData.refund_amount.toString());
      submitData.append("refund_reason", formData.refund_reason || "");
      submitData.append("term", formData.term || "");
      submitData.append("payment_date", formData.payment_date || "");

      // Append file if selected
      if (file) {
        submitData.append("file", file);
      }

      // Log the data being sent for debugging
      console.log("Submitting refund data:");
      console.log("roll_no:", formData.roll_no);
      console.log("reg_id:", formData.reg_id);
      console.log("name:", formData.name);
      console.log("course (ID):", formData.course);
      console.log("technology (ID):", formData.technology);
      console.log("payment_id:", formData.payment_id);
      console.log("refund_amount:", formData.refund_amount);
      console.log("refund_reason:", formData.refund_reason);
      console.log("term:", formData.term);
      console.log("payment_date:", formData.payment_date);

      const response = await api.post(
        `${backendUrl}add-refunds`,
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) {
        toast.success("Refund added successfully!");

        // Reset form
        setFormData({
          payment_id: "",
          roll_no: "",
          reg_id: "",
          name: "",
          course: "",
          technology: "",
          refund_amount: "",
          term: "",
          payment_date: "",
          refund_reason: ""
        });
        setDisplayData({
          course_name: "",
          technology_name: ""
        });
        setFile(null);
        setPaymentFetched(false);

        // Navigate to refunds list page
        setTimeout(() => {
          navigate("/course-refunds-list");
        }, 2000);
      }
    } catch (error) {
      console.error("Error adding refund:", error);
      if (error.response) {
        console.error("Server response:", error.response.data);
        toast.error(`Error: ${error.response.data.message || "Please try again."}`);
      } else if (error.request) {
        console.error("No response received:", error.request);
        toast.error("No response from server. Please check your connection.");
      } else {
        toast.error("Error adding refund. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          transition={Bounce}
        />
        <Col xl="12">
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="card-title mb-0">Add Refund</h4>
                <Button color="secondary" size="sm" onClick={() => navigate("/course-refunds-list")}>
                  Back to Refunds List
                </Button>
              </div>

              <AvForm className="needs-validation" onValidSubmit={handleSubmit}>
                {/* Payment ID Input + Fetch Button */}
                <Row className="mb-3 justify-content-center">
  <Col md="auto" className="d-flex align-items-center">
    <Label htmlFor="payment_id" className="mb-0 me-2">
      Payment ID <span className="text-danger">*</span>
    </Label>
    <AvField
      name="payment_id"
      type="text"
      className="form-control"
      id="payment_id"
      placeholder="Enter payment ID"
      value={formData.payment_id}
      onChange={handleInputChange}
      validate={{
        required: { value: true, errorMessage: "Payment ID is required" }
      }}
      style={{ width: "200px" }} // Optional: set fixed width for consistency
    />
  </Col>
  <Col md="auto" className="d-flex align-items-center">
    <Button
      color="info"
      type="button"
      onClick={fetchPaymentDetails}
      disabled={fetching}
    >
      {fetching ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          Fetching...
        </>
      ) : (
        "Fetch Payment Details"
      )}
    </Button>
  </Col>
</Row>

                {/* Display message if payment not fetched */}
                {!paymentFetched && (
                  <div className="alert alert-info mb-3">
                    Please enter a Payment ID and click "Fetch Payment Details" to load the payment information.
                  </div>
                )}

                {/* Show form fields only after payment details are fetched */}
                {paymentFetched && (
                  <>
                    <div className="mb-3 p-3 bg-light rounded">
                      <h6 className="text-primary">Payment Information (Auto-filled)</h6>
                    </div>

                    <Row>
                      <Col md="3">
                        <div className="mb-3">
                          <Label htmlFor="roll_no">Roll Number</Label>
                          <AvField
                            name="roll_no"
                            type="text"
                            className="form-control"
                            id="roll_no"
                            value={formData.roll_no}
                            readOnly
                          />
                        </div>
                      </Col>
                      <Col md="3">
                        <div className="mb-3">
                          <Label htmlFor="reg_id">Reg ID</Label>
                          <AvField
                            name="reg_id"
                            type="text"
                            className="form-control"
                            id="reg_id"
                            value={formData.reg_id}
                            readOnly
                          />
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="name">Student Name</Label>
                          <AvField
                            name="name"
                            type="text"
                            className="form-control"
                            id="name"
                            value={formData.name}
                            readOnly
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="course_name">Course Name</Label>
                          <AvField
                            name="course_name"
                            type="text"
                            className="form-control"
                            id="course_name"
                            value={displayData.course_name}
                            readOnly
                          />
                          <small className="text-muted">
                            Course ID: {formData.course}
                          </small>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="technology_name">Technology Name</Label>
                          <AvField
                            name="technology_name"
                            type="text"
                            className="form-control"
                            id="technology_name"
                            value={displayData.technology_name}
                            readOnly
                          />
                          <small className="text-muted">
                            Technology ID: {formData.technology}
                          </small>
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="4">
                        <div className="mb-3">
                          <Label htmlFor="term">Term</Label>
                          <AvField
                            name="term"
                            type="text"
                            className="form-control"
                            id="term"
                            value={formData.term}
                            readOnly
                          />
                        </div>
                      </Col>

                      <Col md="4">
                        <div className="mb-3">
                          <Label htmlFor="payment_date">Payment Date</Label>
                          <AvField
                            name="payment_date"
                            type="date"
                            className="form-control"
                            id="payment_date"
                            value={formData.payment_date}
                            readOnly
                          />
                        </div>
                      </Col>

                      <Col md="4">
                        <div className="mb-3">
                          <Label htmlFor="refund_amount">
                            Refund Amount <span className="text-danger">*</span>
                          </Label>
                          <AvField
                            name="refund_amount"
                            type="number"
                            className="form-control"
                            id="refund_amount"
                            placeholder="Enter refund amount"
                            value={formData.refund_amount}
                            readOnly
                            onChange={handleInputChange}
                            validate={{
                              required: { value: true, errorMessage: "Refund amount is required" },
                              min: { value: 1, errorMessage: "Refund amount must be greater than 0" }
                            }}
                          />
                        </div>
                      </Col>
                    </Row>

                    <div className="mb-3 p-3 bg-light rounded">
                      <h6 className="text-primary">Refund Details (Fill these fields)</h6>
                    </div>

                    <Row>


                       <Col md="3">
                        <div className="mb-3">
                          <Label htmlFor="file">Upload Document (Optional)</Label>
                          <input
                            type="file"
                            className="form-control"
                            id="file"
                            onChange={handleFileChange}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          />
                          <small className="form-text text-muted">
                            Supported formats: PDF, DOC, DOCX, JPG, JPEG, PNG
                          </small>
                        </div>
                      </Col>
                     <Col md="4">
  <div className="mb-3">
    <Label htmlFor="refund_reason">
      Refund Reason <span className="text-danger">*</span>
    </Label>
    <AvField
      name="refund_reason"
      type="textarea"
      className="form-control"
      id="refund_reason"
      rows="3"
      placeholder="Enter refund reason"
      value={formData.refund_reason}
      onChange={handleInputChange}
      validate={{
        required: { value: true, errorMessage: "Refund reason is required" }
      }}
      style={{
        resize: "none", // Disables resizing
        overflow: "auto", // Shows scrollbar when needed
        minHeight: "42px", // Minimum height
        maxHeight: "42px" // Maximum height before scrolling
      }}
    />
  </div>
</Col>
                    </Row>





                    <div className="d-flex gap-2 mt-4">
                      <Button color="primary" type="submit" disabled={loading}>
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Adding Refund...
                          </>
                        ) : (
                          "Add Refund"
                        )}
                      </Button>
                      <Button color="secondary" type="button" onClick={() => navigate("/course-refunds-list")} disabled={loading}>
                        Cancel
                      </Button>
                    </div>
                  </>
                )}
              </AvForm>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default AddRefunds;
